// Public realtime counters widget (WEB3 · I6).
//
// Three rolling numbers — pieces / authors / countries. HTTP polling against
// `GET /counters` (server-side 30s cache). The previous WS path is retired:
// `/counters/ws` is 503 by design (see apps/api/src/counters.rs::handle_ws —
// the DO snapshot is no longer authoritative, counters are recomputed from
// D1 on every read). #WEB56.

import { createSignal, createMemo, onCleanup, onMount, For } from "solid-js";

interface Snapshot {
  pieces: number;
  authors: number;
  countries: number;
  updated_at_ms: number;
}

const ZERO: Snapshot = { pieces: 0, authors: 0, countries: 0, updated_at_ms: 0 };
const POLL_INTERVAL_MS = 30_000;

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").trim();

function httpUrl(path: string): string {
  return `${API_BASE}${path}`;
}

interface Labels {
  pieces: string;
  authors: string;
  countries: string;
}

interface Props {
  labels: Labels;
  /** Inline horizontal row sized to live inside the header. */
  compact?: boolean;
}

export default function Counters(props: Props) {
  const [snap, setSnap] = createSignal<Snapshot>(ZERO);

  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const applyJson = (raw: unknown) => {
    if (raw && typeof raw === "object") {
      const r = raw as Partial<Snapshot>;
      setSnap({
        pieces: Number(r.pieces ?? 0),
        authors: Number(r.authors ?? 0),
        countries: Number(r.countries ?? 0),
        updated_at_ms: Number(r.updated_at_ms ?? Date.now()),
      });
    }
  };

  const pollOnce = async () => {
    if (!API_BASE) return;
    try {
      const res = await fetch(httpUrl("/counters"));
      if (!res.ok) return;
      applyJson(await res.json());
    } catch {
      // network blip — leave the last snapshot in place
    }
  };

  onMount(() => {
    pollOnce();
    pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);
  });

  onCleanup(() => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  });

  const tiles = createMemo(() => [
    { label: props.labels.pieces, value: snap().pieces },
    { label: props.labels.authors, value: snap().authors },
    { label: props.labels.countries, value: snap().countries },
  ]);

  if (props.compact) {
    return (
      <div class="flex items-baseline gap-4 font-display">
        <For each={tiles()}>
          {(t) => (
            <div class="flex items-baseline gap-1.5">
              <span class="text-amber-200 text-base sm:text-lg tabular-nums">
                <RollingNumber value={t.value} compact />
              </span>
              <span class="uppercase tracking-widest text-slate-300 text-[10px]">
                {t.label}
              </span>
            </div>
          )}
        </For>
      </div>
    );
  }

  return (
    <div class="w-full max-w-[1200px] grid grid-cols-3 gap-3 sm:gap-6 text-center">
      <For each={tiles()}>
        {(t) => (
          <div class="rounded-xl bg-slate-900/60 border border-slate-800 px-3 py-4 sm:py-5">
            <RollingNumber value={t.value} />
            <div class="mt-1 text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-display">
              {t.label}
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

// Animated rolling number — interpolates from the previous value to the
// new one over ~600ms using requestAnimationFrame. Cheap, no deps.
function RollingNumber(props: { value: number; compact?: boolean }) {
  const [display, setDisplay] = createSignal(props.value);
  let rafId: number | null = null;
  let from = props.value;
  let to = props.value;
  let startTs = 0;

  const tick = (ts: number) => {
    if (!startTs) startTs = ts;
    const t = Math.min(1, (ts - startTs) / 600);
    const eased = 1 - Math.pow(1 - t, 3);
    setDisplay(Math.round(from + (to - from) * eased));
    if (t < 1) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = null;
    }
  };

  // Track prop changes by reading on every render.
  const tracked = createMemo(() => props.value);
  // Effect-ish: kick off animation whenever the tracked value changes.
  createMemo(() => {
    const next = tracked();
    if (next === to) return;
    from = display();
    to = next;
    startTs = 0;
    if (rafId == null) rafId = requestAnimationFrame(tick);
  });

  onCleanup(() => {
    if (rafId != null) cancelAnimationFrame(rafId);
  });

  if (props.compact) {
    return <span>{display().toLocaleString()}</span>;
  }
  return (
    <div class="font-display text-2xl sm:text-4xl tabular-nums text-amber-300">
      {display().toLocaleString()}
    </div>
  );
}
